import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { getAuthToken, setCookie } from "../utils/common";
import { fetchMyPermissions } from "../services/rbac";
import { selectLoginIdentifier } from "../services/login";


const UserContext = createContext();


const normalizePermissionContext = (raw) => {
  if (!raw) return null;
  return {
    persona: raw.persona,
    isDeveloperAdmin: Boolean(raw.isDeveloperAdmin ?? raw.is_developer_admin),
    projectIds: raw.projectIds ?? raw.project_ids ?? [],
    byProject: raw.permissionsByProject ?? raw.permissions_by_project ?? raw.byProject ?? {},
    global: raw.globalPermissions ?? raw.global_permissions ?? [],
  };
};


export const UserProvider = ({ children }) => {

  const [userInfo, setUserInfo] = useState(() => {
    const storedUserInfo = sessionStorage.getItem("userInfo");

    return storedUserInfo ? JSON.parse(storedUserInfo) : getAuthToken();
  });

  useEffect(() => {
    // Save the userInfo to sessionStorage whenever it changes
    if (userInfo) {
      sessionStorage.setItem("userInfo", JSON.stringify(userInfo));
    } else {
      sessionStorage.removeItem("userInfo");
    }
  }, [userInfo]);

  // Hydrate permissions from BE whenever we have an authenticated user but
  // no permission context yet (e.g. first login or page reload).
  useEffect(() => {
    if (!userInfo) return;
    if (userInfo.permissions) return;
    let cancelled = false;
    fetchMyPermissions()
      .then((res) => {
        if (cancelled) return;
        const normalized = normalizePermissionContext(res);
        if (normalized) {
          setUserInfo((prev) => (prev ? { ...prev, permissions: normalized } : prev));
        }
      })
      .catch((err) => {
        console.warn("Failed to load /me/permissions:", err?.message || err);
      });
    return () => {
      cancelled = true;
    };
  }, [userInfo?.loginId, userInfo?.accessToken]); // eslint-disable-line react-hooks/exhaustive-deps

  // Subscription billing is post-paid (inventory-based); the old pre-paid quota lockout
  // (currentUsage > totalQuota) was removed, so there is no read-only mode. Kept as a
  // constant so existing consumers (Buttons, project/property screens) stay wired.
  const isReadOnly = false;

  const Trial = userInfo?.loginIdentifierDetails?.subscription?.subscriptionType?.typeName === "TRIAL";

  // Keep a ref to the latest userInfo so switchLoginIdentifier can read
  // the previously-cached identifierLinks without re-binding on each
  // userInfo change (which would invalidate downstream useCallback deps).
  const userInfoRef = useRef(userInfo);
  useEffect(() => {
    userInfoRef.current = userInfo;
  }, [userInfo]);

  /**
   * Swap the active identifier for the current login. Calls the BE
   * (which validates the link belongs to this login and re-issues a JWT),
   * rewires the accessToken cookie that axios reads from, and updates
   * userInfo so downstream screens re-render against the new org.
   *
   * Caller is expected to refresh any in-memory list state OR force a
   * reload — keeping this layer cheap and side-effect-free for now.
   */
  const switchLoginIdentifier = useCallback(async ({ linkId, remember = false }) => {
    const fresh = await selectLoginIdentifier({ linkId, remember });
    if (!fresh?.accessToken || !fresh?.persona) {
      throw new Error("select-identifier returned an unexpected payload");
    }
    // Persist the new bearer first — axios interceptor will pick it up
    // from the cookie on the next request.
    setCookie("_USER_AUTH_", fresh);
    setCookie("accessToken", fresh.accessToken, { path: "/", expires: 1 });
    // Cache identifier links on userInfo so the topbar chip can cheaply
    // decide whether to render as a clickable switcher without refetching.
    // BE returns `links` on /login/select-identifier; fall back to the
    // previously cached list so a single missing field doesn't make the
    // chip lose its multi-org affordance mid-session.
    const nextUserInfo = {
      ...fresh,
      identifierLinks:
        Array.isArray(fresh.links)
          ? fresh.links
          : userInfoRef.current?.identifierLinks || [],
    };
    setUserInfo(nextUserInfo);
    return nextUserInfo;
  }, []);

  return (
    <UserContext.Provider value={{ userInfo, setUserInfo, isReadOnly, Trial, switchLoginIdentifier }}>
      {children}
    </UserContext.Provider>
  );
};

// Custom hook to access user context
export const useUser = () => useContext(UserContext);
